import { Icon } from '@iconify/react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Selection,
} from '@nextui-org/react';
import React from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { SelectAllCheckbox } from '@/components/select-all-checkbox';
import {
  COHORT_DIVIDER_KEY,
  SELECT_ALL_COHORTS_KEY,
} from '@/components/cohorts/cohort-multi-select';
import {
  areAllSelectAllTargetsSelected,
  getFilterSelectAllCheckState,
  getVisibleFilterSelectedKeys,
  normalizeFilterSelectionChange,
  toggleFilterSelectAll,
} from './filter-utils';
import SearchInput from './search-input';
import { FilterOption, TableConfig, TableFilter } from './types';
import { capitalize } from './utils';

// Tables search on every keystroke unless they opt into a debounce.
const DEFAULT_SEARCH_DEBOUNCE_MS = 0;

function getFilterOptionLabel(
  option: FilterOption,
  selected: Selection | undefined,
  filter: TableFilter
): string {
  if (option.variant === 'select-all') {
    return areAllSelectAllTargetsSelected(selected, filter)
      ? 'All selected'
      : 'Select all';
  }

  return option.name;
}

interface TopContentProps {
  config: TableConfig;
  filterValue: string;
  visibleColumns: Selection;
  filters: Record<string, Selection>;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: string, values: Selection) => void;
  onVisibleColumnsChange: (columns: Selection) => void;
  totalItems: number;
  rowsPerPage: number;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function TopContent({
  config,
  filterValue,
  visibleColumns,
  filters,
  onSearchChange,
  onFilterChange,
  onVisibleColumnsChange,
  totalItems,
  rowsPerPage,
  onRowsPerPageChange,
}: TopContentProps) {
  const isSmUp = useMediaQuery('(min-width: 640px)');

  const showSearch = Boolean(config.searchable);
  const showSelectionToolbar = Boolean(config.selectionToolbar);
  const showFilters = isSmUp && Boolean(config.filters?.length);
  const showColumnSelection =
    isSmUp && Boolean(config.actions?.enableColumnSelection);
  const showAdd = Boolean(config.actions?.enableAdd);
  const showActions = showFilters || showColumnSelection || showAdd;
  const showToolbar = showSearch || showActions || showSelectionToolbar;

  const rowsPerPageOptions = config.pagination?.rowsPerPageOptions ?? [];
  const showTotalItems = Boolean(config.displayTotalItems);
  const showRowsPerPage =
    Boolean(config.pagination?.enabled) && rowsPerPageOptions.length > 0;
  const showFooterMeta = showTotalItems || showRowsPerPage;

  if (!showToolbar && !showFooterMeta) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {showToolbar && (
        <div
          className={`flex items-end ${showSearch && showActions ? 'justify-between' : showActions ? 'justify-end' : ''}`}
        >
          {showSearch && (
            <div className="flex w-full flex-col gap-2 sm:max-w-[44%]">
              <SearchInput
                initialValue={filterValue}
                placeholder={config.searchPlaceholder}
                delay={config.searchDebounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS}
                onSearch={onSearchChange}
              />
              {config.selectionToolbar}
            </div>
          )}
          {!showSearch && config.selectionToolbar && (
            <div className="w-full sm:max-w-[44%]">{config.selectionToolbar}</div>
          )}
          {showActions && (
            <div className="flex gap-3">
              {showFilters &&
                config.filters?.map((filter) => (
                  <div
                    key={filter.key}
                    className="ml-3 flex items-center gap-2"
                  >
                    {filter.leadingContent}
                    <Dropdown placement={filter.popoverPlacement ?? 'bottom-start'}>
                      <DropdownTrigger>
                        <Button
                          endContent={<Icon icon="mdi:chevron-down" />}
                          variant="flat"
                        >
                          {filter.label}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label={filter.label}
                        closeOnSelect={false}
                        selectedKeys={getVisibleFilterSelectedKeys(
                          filters[filter.key],
                          filter
                        )}
                        selectionMode="multiple"
                        onSelectionChange={(keys) => {
                          if (keys !== 'all') {
                            const keySet = new Set(
                              Array.from(
                                keys as Exclude<Selection, 'all'>,
                                String
                              )
                            );

                            if (
                              keySet.has(SELECT_ALL_COHORTS_KEY) ||
                              keySet.has(COHORT_DIVIDER_KEY)
                            ) {
                              return;
                            }
                          }

                          onFilterChange(
                            filter.key,
                            normalizeFilterSelectionChange(
                              keys,
                              filters[filter.key],
                              filter
                            )
                          );
                        }}
                      >
                        {filter.options.map((option) => {
                          if (option.variant === 'divider') {
                            return (
                              <DropdownItem
                                key={option.uid}
                                isReadOnly
                                textValue=""
                                className="min-h-0 h-2 rounded-none px-0 py-0 opacity-100 data-[hover=true]:bg-transparent pointer-events-none"
                                hideSelectedIcon
                              >
                                <div className="h-px w-[calc(100%+16px)] -mx-2 bg-divider" />
                              </DropdownItem>
                            );
                          }

                          if (option.variant === 'select-all') {
                            const selectAllCheckState =
                              getFilterSelectAllCheckState(
                                filters[filter.key],
                                filter
                              );

                            return (
                              <DropdownItem
                                key={option.uid}
                                hideSelectedIcon
                                textValue={getFilterOptionLabel(
                                  option,
                                  filters[filter.key],
                                  filter
                                )}
                                onPress={() => {
                                  onFilterChange(
                                    filter.key,
                                    toggleFilterSelectAll(
                                      filters[filter.key],
                                      filter
                                    )
                                  );
                                }}
                              >
                                <span className="flex items-center gap-2">
                                  <SelectAllCheckbox state={selectAllCheckState} />
                                  {getFilterOptionLabel(
                                    option,
                                    filters[filter.key],
                                    filter
                                  )}
                                </span>
                              </DropdownItem>
                            );
                          }

                          return (
                            <DropdownItem
                              key={option.uid}
                              className="capitalize"
                            >
                              {option.name}
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                ))}

              {showColumnSelection && (
                <Dropdown className="ml-3">
                  <DropdownTrigger>
                    <Button
                      endContent={<Icon icon="mdi:chevron-down" />}
                      variant="flat"
                    >
                      Columns
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    disallowEmptySelection
                    aria-label="Table Columns"
                    closeOnSelect={false}
                    selectedKeys={visibleColumns}
                    selectionMode="multiple"
                    onSelectionChange={onVisibleColumnsChange}
                  >
                    {config.columns.map((column) => (
                      <DropdownItem key={column.uid} className="capitalize">
                        {capitalize(column.name)}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}

              {showAdd && (
                <Button
                  color="primary"
                  endContent={<Icon icon="mdi:plus" />}
                  onPress={config.actions?.onAdd}
                  className="ml-3"
                >
                  {config.actions?.addButtonLabel || 'Add New'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {showFooterMeta && (
        <div
          className={`flex items-center ${showTotalItems && showRowsPerPage ? 'justify-between' : showRowsPerPage ? 'justify-end' : ''}`}
        >
          {showTotalItems && (
            <span className="text-default-400 text-small">
              Total {totalItems} items
            </span>
          )}
          {showRowsPerPage && (
            <label className="flex items-center text-default-400 text-small">
              Rows per page:
              <select
                className="bg-transparent outline-none text-default-400 text-small"
                onChange={onRowsPerPageChange}
                value={rowsPerPage}
              >
                {rowsPerPageOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
